// Flow 4 - guided workflow validation before a build stage (D35;
// docs/phase-0/slice5-ux-flows.md §7). ONE component mounts at BOTH
// `/apps/new/build/workflow` (pre-MVP, wizard-scoped via `useWizard()`) and
// `/apps/:appId/build/workflow` (the re-run before Production Build, route-param
// scoped) - the re-run is visually IDENTICAL beyond the item count and subtitle copy
// (ux spec §7.2 "no visual difference... beyond the item count"), never a second widget
// for the same "confirm a sequence" shape.
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { isErr } from "@nightcrew/shared";
import { AppId } from "@nightcrew/shared";
import type { AppView, WorkflowStep } from "@nightcrew/shared";
import { FocusedFlowShell } from "../../components/FocusedFlowShell.js";
import { BuildChecklistItem } from "../../components/BuildChecklistItem.js";
import { getWorkflowValidation, submitWorkflowValidation } from "../../api/build.js";
import { getApp } from "../../api/apps.js";
import { buildCopy } from "../../copy/build.js";
import { isViewerRole, useSession } from "../../state/session.js";
import { useWizard } from "../../state/wizard.js";

type ScreenState =
  | { readonly kind: "loading" }
  | { readonly kind: "error" }
  | { readonly kind: "ready"; readonly app: AppView; readonly steps: readonly WorkflowStep[] };

export const WorkflowValidation = (): React.JSX.Element => {
  const { state: session } = useSession();
  const { draft } = useWizard();
  const params = useParams<{ appId?: string }>();
  const navigate = useNavigate();

  const parsedRouteAppId = params.appId !== undefined ? AppId.safeParse(params.appId) : null;
  const rerunAppId = parsedRouteAppId?.success === true ? parsedRouteAppId.data : null;
  const isRerun = rerunAppId !== null;
  const appId = rerunAppId ?? draft.appId;

  const [state, setState] = useState<ScreenState>({ kind: "loading" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (appId === null) return;
    const cancelled = { current: false };
    setState({ kind: "loading" });
    void (async () => {
      const [appResult, workflowResult] = await Promise.all([
        getApp(appId),
        getWorkflowValidation(appId),
      ]);
      if (cancelled.current) return;
      if (isErr(appResult) || isErr(workflowResult)) {
        setState({ kind: "error" });
        return;
      }
      setState({ kind: "ready", app: appResult.value, steps: workflowResult.value.steps });
    })();
    return () => {
      cancelled.current = true;
    };
  }, [appId]);

  if (appId === null) {
    return (
      <FocusedFlowShell title={buildCopy("build.workflow.titleGeneric")}>
        <div className="card state-card">
          <p>Start from the beginning.</p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              void navigate("/apps/new");
            }}
          >
            Back
          </button>
        </div>
      </FocusedFlowShell>
    );
  }

  if (state.kind === "loading") {
    return (
      <FocusedFlowShell title={buildCopy("build.workflow.titleGeneric")}>
        <div className="stack gap12">
          <div className="skeleton skeleton-line" />
          <div className="skeleton skeleton-line" />
          <div className="skeleton skeleton-line" />
        </div>
      </FocusedFlowShell>
    );
  }

  if (state.kind === "error") {
    return (
      <FocusedFlowShell title={buildCopy("build.workflow.titleGeneric")}>
        <div className="card state-card">
          <p>{buildCopy("build.workflow.error")}</p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setState({ kind: "loading" });
            }}
          >
            {buildCopy("build.errors.generic.retryCta")}
          </button>
        </div>
      </FocusedFlowShell>
    );
  }

  const { app, steps } = state;
  const viewer = isViewerRole(session);
  const viewerReason = viewer ? buildCopy("build.workflow.deniedRole", { appName: app.name }) : undefined;
  const allConfirmed = steps.length > 0 && steps.every((step) => step.confirmed);

  const persist = (nextSteps: readonly WorkflowStep[]): void => {
    setState({ kind: "ready", app, steps: nextSteps });
    void submitWorkflowValidation(appId, [...nextSteps]);
  };

  const handleConfirm = (id: string): void => {
    persist(steps.map((step) => (step.id === id ? { ...step, confirmed: true } : step)));
  };

  const handleAdjust = (id: string, nextLabel: string): void => {
    persist(steps.map((step) => (step.id === id ? { ...step, label: nextLabel, confirmed: true } : step)));
  };

  const handleContinue = (): void => {
    setSubmitting(true);
    void (async () => {
      await submitWorkflowValidation(appId, [...steps]);
      setSubmitting(false);
      void navigate(isRerun ? `/apps/${appId}/build/production` : "/apps/new/build");
    })();
  };

  return (
    <FocusedFlowShell
      title={buildCopy("build.workflow.title", { appName: app.name })}
      onBack={() => {
        void navigate(isRerun ? `/apps/${appId}/build` : "/apps/new/brand/mockups");
      }}
      cta={
        <button
          type="button"
          className="btn btn-primary"
          disabled={!allConfirmed || submitting || viewer}
          aria-describedby={!allConfirmed ? "workflow-continue-reason" : undefined}
          onClick={handleContinue}
        >
          {buildCopy("build.workflow.continueCta")}
        </button>
      }
    >
      <h1 className="h2">{buildCopy("build.workflow.title", { appName: app.name })}</h1>
      <p className="muted mt8">
        {buildCopy(isRerun ? "build.workflow.rerunSubtitle" : "build.workflow.subtitle", {
          appName: app.name,
        })}
      </p>
      {!allConfirmed ? (
        <p className="sr-only" id="workflow-continue-reason">
          {buildCopy("build.workflow.continueDisabledReason")}
        </p>
      ) : null}
      <ol className="workflow-list mt16" style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {steps.map((step) => (
          <BuildChecklistItem
            key={step.id}
            statement={step.label}
            resolved={step.confirmed}
            onConfirm={() => {
              handleConfirm(step.id);
            }}
            onAdjust={(next) => {
              handleAdjust(step.id, next);
            }}
            {...(viewerReason !== undefined ? { disabledReason: viewerReason } : {})}
          />
        ))}
      </ol>
    </FocusedFlowShell>
  );
};
